import { normalizeAuth } from './requestAuth';
import { normalizeRequestBodyMode } from './requestBodyMode';
import { protocolFieldsOf } from './requestProtocolFields';

const SNAPSHOT_VERSION = 7;

export const TAB_STRIP_TYPES = ['request', 'collection', 'schema', 'db-manager-table'];

// Accepts either `{ requestTabs, ... }` (persistence callers) or maps keyed by
// strip type (`{ request, 'db-manager-table', ... }` — EnhancedTabManager's
// tabMaps shape, which the old destructured signature silently ignored).
function toTabMaps(tabSets = {}) {
  return {
    request: tabSets.requestTabs || tabSets.request || [],
    collection: tabSets.collectionTabs || tabSets.collection || [],
    schema: tabSets.schemaTabs || tabSets.schema || [],
    'db-manager-table': tabSets.dbManagerTabs || tabSets['db-manager-table'] || [],
  };
}

export function buildDefaultTabStripOrder(tabSets = {}) {
  const tabMaps = toTabMaps(tabSets);
  return TAB_STRIP_TYPES.flatMap((type) => tabMaps[type].map((tab) => ({ type, id: tab.id })));
}

export function normalizeTabStripOrder(order, tabSets) {
  const tabMaps = toTabMaps(tabSets);
  const seen = new Set();
  const normalized = [];

  for (const entry of Array.isArray(order) ? order : []) {
    if (!entry || typeof entry !== 'object') continue;
    const { type, id } = entry;
    if (!TAB_STRIP_TYPES.includes(type) || id == null) continue;
    const key = `${type}:${id}`;
    if (seen.has(key)) continue;
    const tabs = tabMaps[type];
    if (!tabs?.some((tab) => String(tab.id) === String(id))) continue;
    seen.add(key);
    normalized.push({ type, id });
  }

  for (const type of TAB_STRIP_TYPES) {
    for (const tab of tabMaps[type] || []) {
      const key = `${type}:${tab.id}`;
      if (!seen.has(key)) {
        seen.add(key);
        normalized.push({ type, id: tab.id });
      }
    }
  }

  return normalized;
}

export function appendTabStripEntry(order, entry) {
  return [...(order || []), entry];
}

export function insertTabStripEntryAfter(order, refType, refId, entry) {
  const list = order || [];
  const idx = list.findIndex(
    (e) => e.type === refType && String(e.id) === String(refId)
  );
  if (idx === -1) return [...list, entry];
  const next = [...list];
  next.splice(idx + 1, 0, entry);
  return next;
}

export function removeTabStripEntry(order, type, id) {
  return (order || []).filter(
    (e) => !(e.type === type && String(e.id) === String(id))
  );
}

export function getTabStripSortableId(entry) {
  return `${entry.type}-${entry.id}`;
}

const DEFAULT_KV_ROW = { key: '', value: '', enabled: true };

const REQUEST_EDITOR_TABS = ['params', 'authorization', 'headers', 'cookies', 'body', 'script'];
const LEGACY_SCRIPT_EDITOR_TABS = ['pre-request', 'tests'];
const RESPONSE_VIEWER_TABS = ['body', 'headers', 'cookies', 'test-results'];

export function normalizeEditorTab(value) {
  if (REQUEST_EDITOR_TABS.includes(value)) return value;
  if (LEGACY_SCRIPT_EDITOR_TABS.includes(value)) return 'script';
  return 'params';
}

export function normalizeResponseTab(value) {
  return RESPONSE_VIEWER_TABS.includes(value) ? value : 'body';
}

function cloneKvRows(rows) {
  if (!Array.isArray(rows)) return [{ ...DEFAULT_KV_ROW }];
  return rows.map((row) => ({ ...row }));
}

function clonePlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return { ...value };
}

/**
 * Normalize the per-request `mock` slot. Backward-compatible: missing/invalid
 * input becomes `{ responseExamples: [] }`. The `expectations`/`schema`/`smart`
 * keys are reserved for later phases and passed through untouched when present.
 */
/**
 * The request kinds RequestBuilder knows how to render.
 *
 * RequestBuilder branches on `initialData.type` for 'sse' / 'grpc' /
 * 'websocket' and falls through to the HTTP editor for everything else, so an
 * unrecognised value is not an error — it is silently an HTTP request. Keeping
 * the list in one place means the persistence layers and any tool that creates
 * a request agree on what is valid.
 */
export const REQUEST_KINDS = ['http', 'websocket', 'sse', 'grpc'];

export function normalizeRequestKind(value) {
  const kind = String(value || '').toLowerCase();
  return REQUEST_KINDS.includes(kind) ? kind : 'http';
}

export function normalizeMock(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { responseExamples: [] };
  }
  const responseExamples = Array.isArray(value.responseExamples)
    ? value.responseExamples.map(normalizeMockExample)
    : [];
  const out = { ...value, responseExamples };
  return out;
}

function normalizeMockExample(ex) {
  if (!ex || typeof ex !== 'object') return ex;
  return {
    id: ex.id,
    name: ex.name ?? '',
    status: Number(ex.status) || 200,
    contentType: ex.contentType ?? 'application/json',
    headers: cloneKvRows(ex.headers),
    body: ex.body ?? '',
    matchQuery: cloneKvRows(ex.matchQuery),
    matchHeaders: cloneKvRows(ex.matchHeaders),
    // Enabled by default; old examples that used `isDefault` stay enabled too.
    enabled: ex.enabled !== false,
    // Smart Mock: 'example' (static body) or 'schema' (generated via generateSchemaMock).
    sourceType: ex.sourceType === 'schema' ? 'schema' : 'example',
    schemaId: ex.schemaId ?? null,
  };
}

/** Persist last response for a tab (stored in AppLocalData temp files). */
export function serializeResponse(response) {
  if (!response || typeof response !== 'object') return null;

  return {
    status: response.status ?? 0,
    statusText: response.statusText ?? response.status_text ?? '',
    headers: clonePlainObject(response.headers),
    data: response.data ?? '',
    time: response.time ?? null,
    size: response.size ?? null,
    byteMetrics: response.byteMetrics ? { ...response.byteMetrics } : undefined,
    truncated: Boolean(response.truncated),
  };
}

/** Persist full request editor state for a tab (body, params, auth, last response, etc.). */
export function serializeRequestTab(tab) {
  if (!tab || typeof tab !== 'object') return null;
  return {
    id: tab.id,
    name: tab.name ?? null,
    method: tab.method || 'GET',
    url: tab.url || '',
    headers: cloneKvRows(tab.headers),
    params: cloneKvRows(tab.params),
    cookies: cloneKvRows(tab.cookies),
    body: tab.body ?? '',
    ...(() => {
      const { bodyType, rawBodyLanguage } = normalizeRequestBodyMode(tab);
      return { bodyType, rawBodyLanguage };
    })(),
    formDataFields: tab.formDataFields ?? null,
    auth: normalizeAuth(tab.auth, { forRequest: true }),
    preRequestScript: tab.preRequestScript ?? '',
    testScript: tab.testScript ?? '',
    // Local Mock definitions (response examples). Local-only — never published.
    mock: normalizeMock(tab.mock),
    collectionId: tab.collectionId ?? null,
    originalId: tab.originalId ?? null,
    isNewRequest: Boolean(tab.isNewRequest),
    hasUnsavedChanges: Boolean(tab.hasUnsavedChanges),
    description: tab.description || '',
    loading: false,
    response: serializeResponse(tab.response),
    // The request KIND. Dropped here until 18.08.2026, so a WebSocket tab came
    // back from a restart as a plain HTTP request — RequestBuilder switches its
    // whole editor on this value and silently renders the HTTP one when it is
    // absent. Normalised rather than trusted: an unknown kind is 'http', not a
    // builder that fails to render at all.
    type: normalizeRequestKind(tab.type),
    // A gRPC / WebSocket tab's own fields — an open, unsaved one came back empty (card 5258c71d).
    ...protocolFieldsOf({ ...tab, type: normalizeRequestKind(tab.type) }),
    editorTab: normalizeEditorTab(tab.editorTab),
    responseTab: normalizeResponseTab(tab.responseTab),
  };
}

export function hydrateRequestTab(tab) {
  if (!tab || typeof tab !== 'object') return null;
  return serializeRequestTab(tab);
}

function serializeSchemaTab(tab) {
  if (!tab || typeof tab !== 'object') return tab;
  return {
    id: tab.id,
    type: 'schema',
    schemaId: tab.schemaId ?? null,
    name: tab.name || 'Schema',
    rootType: tab.rootType === 'array' ? 'array' : 'object',
    nodes: Array.isArray(tab.nodes) ? tab.nodes : [],
    hasUnsavedChanges: Boolean(tab.hasUnsavedChanges),
  };
}

function serializeCollectionTab(tab) {
  if (!tab || typeof tab !== 'object') return tab;
  return {
    id: tab.id,
    type: 'collection',
    collectionId: tab.collectionId,
    name: tab.name || 'Collection',
    section: tab.section || 'docs',
    documentation: tab.documentation || '',
    auth: tab.auth || { type: 'noauth' },
    variables: cloneKvRows(tab.variables),
    preRequestScript: typeof tab.preRequestScript === 'string' ? tab.preRequestScript : '',
    testScript: typeof tab.testScript === 'string' ? tab.testScript : '',
    // A folder page (owner D1) is a collection tab that names its folder.
    ...(tab.folderId != null && tab.folderId !== '' ? { folderId: String(tab.folderId) } : {}),
    hasUnsavedChanges: Boolean(tab.hasUnsavedChanges),
  };
}

// Persisted so a DB Manager table tab survives a restart — deliberately only
// the tab's identity/drafts, never result rows or pending edits. The connId it
// references dies with the process; hydration marks these `needsReconnect` and
// DbManagerResultsTab shows a reconnect screen instead of querying it.
function serializeDbManagerTab(tab) {
  if (!tab || typeof tab !== 'object') return tab;
  return {
    id: tab.id,
    type: 'db-manager-table',
    extensionId: tab.extensionId,
    connId: tab.connId,
    engine: tab.engine || 'mysql',
    dbName: tab.dbName ?? null,
    tableName: tab.tableName ?? null,
    label: tab.label || tab.tableName || 'Query',
    filterText: tab.filterText || '',
    sql: tab.sql || '',
    scriptName: tab.scriptName ?? null,
    scriptLinked: tab.scriptLinked !== false,
    // Per-tab grid page size (100/500/1000/5000); the component falls back to
    // its default for anything non-numeric.
    pageSize: typeof tab.pageSize === 'number' ? tab.pageSize : null,
    columnWidths: tab.columnWidths && typeof tab.columnWidths === 'object' ? tab.columnWidths : null,
    transposedWidths: tab.transposedWidths && typeof tab.transposedWidths === 'object' ? tab.transposedWidths : null,
  };
}

/**
 * A database overview tab (store/dbOverviewTabs.js) is a view of the live
 * catalog, not a draft: it is reopened from the tree in one double-click, and
 * restored it would only be a reconnect screen. It is never saved, and one in
 * an older snapshot is dropped on load — serialised it would come back as a
 * blank query tab (the serializer keeps no `view`), hence the id check too.
 */
function isDbOverviewTabRecord(tab) {
  return !!tab && (tab.view === 'overview' || String(tab.id ?? '').startsWith('db-manager-overview:'));
}

export function sanitizeSnapshotForStorage(snapshot) {
  const requestTabs = (snapshot.requestTabs || [])
    .map(serializeRequestTab)
    .filter(Boolean);
  const collectionTabs = (snapshot.collectionTabs || []).map(serializeCollectionTab);
  const schemaTabs = (snapshot.schemaTabs || []).map(serializeSchemaTab);
  const dbManagerTabs = (snapshot.dbManagerTabs || [])
    .filter((tab) => !isDbOverviewTabRecord(tab))
    .map(serializeDbManagerTab)
    .filter((tab) => tab && tab.extensionId && tab.connId);

  const tabStripOrder = normalizeTabStripOrder(snapshot.tabStripOrder, {
    requestTabs,
    collectionTabs,
    schemaTabs,
    dbManagerTabs,
  });

  // Legacy snapshots may carry an `environment` activeTabType; coerce it.
  const activeTabType =
    snapshot.activeTabType && snapshot.activeTabType !== 'environment'
      ? snapshot.activeTabType
      : 'request';

  return {
    version: SNAPSHOT_VERSION,
    requestTabs,
    collectionTabs,
    schemaTabs,
    dbManagerTabs,
    tabStripOrder,
    activeRequestTabId: snapshot.activeRequestTabId ?? null,
    activeCollectionTabId: snapshot.activeCollectionTabId ?? null,
    activeSchemaTabId: snapshot.activeSchemaTabId ?? null,
    activeDbManagerTabId: snapshot.activeDbManagerTabId ?? null,
    activeTabType,
    savedAt: Date.now(),
  };
}

function pickActiveTabId(preferredId, tabs) {
  if (!tabs?.length) return null;
  if (preferredId != null && tabs.some((tab) => String(tab.id) === String(preferredId))) {
    return preferredId;
  }
  return tabs[0]?.id ?? null;
}

export function normalizeSnapshot(entry) {
  const requestTabs = (Array.isArray(entry.requestTabs) ? entry.requestTabs : [])
    .map(hydrateRequestTab)
    .filter(Boolean);
  const collectionTabs = (Array.isArray(entry.collectionTabs) ? entry.collectionTabs : []).map(
    serializeCollectionTab
  );
  const schemaTabs = (Array.isArray(entry.schemaTabs) ? entry.schemaTabs : []).map(
    serializeSchemaTab
  );
  const dbManagerTabs = (Array.isArray(entry.dbManagerTabs) ? entry.dbManagerTabs : [])
    .filter((tab) => !isDbOverviewTabRecord(tab))
    .map(serializeDbManagerTab)
    .filter((tab) => tab && tab.extensionId && tab.connId);

  // Legacy snapshots may carry an `environment` activeTabType; coerce it.
  // Extension panel tab types (`db-manager-table`, `task-manager-board`, `projects-project`) are
  // also coerced: on hydration no extension panel is activated yet, so the
  // app always lands on a built-in strip first.
  let activeTabType =
    entry.activeTabType && entry.activeTabType !== 'environment' ? entry.activeTabType : 'request';
  if (activeTabType === 'db-manager-table' || activeTabType === 'task-manager-board' || activeTabType === 'projects-project') activeTabType = 'request';
  let activeRequestTabId = pickActiveTabId(entry.activeRequestTabId, requestTabs);
  let activeCollectionTabId = pickActiveTabId(entry.activeCollectionTabId, collectionTabs);
  let activeSchemaTabId = pickActiveTabId(entry.activeSchemaTabId, schemaTabs);
  const activeDbManagerTabId = pickActiveTabId(entry.activeDbManagerTabId, dbManagerTabs);

  if (activeTabType === 'collection' && !activeCollectionTabId) {
    activeTabType = 'request';
  }
  if (activeTabType === 'schema' && !activeSchemaTabId) {
    activeTabType = 'request';
  }
  if (activeTabType === 'request' && requestTabs.length && !activeRequestTabId) {
    activeRequestTabId = requestTabs[0].id;
  }

  const tabStripOrder = normalizeTabStripOrder(entry.tabStripOrder, {
    requestTabs,
    collectionTabs,
    schemaTabs,
    dbManagerTabs,
  });

  return {
    version: entry.version ?? SNAPSHOT_VERSION,
    requestTabs,
    collectionTabs,
    schemaTabs,
    dbManagerTabs,
    tabStripOrder,
    activeRequestTabId,
    activeCollectionTabId,
    activeSchemaTabId,
    activeDbManagerTabId,
    activeTabType,
  };
}

export function buildWorkspaceTabSnapshot(state) {
  return sanitizeSnapshotForStorage({
    requestTabs: state.requestTabs,
    collectionTabs: state.collectionTabs,
    schemaTabs: state.schemaTabs,
    dbManagerTabs: state.dbManagerTabs,
    tabStripOrder: state.tabStripOrder,
    activeRequestTabId: state.activeRequestTabId,
    activeCollectionTabId: state.activeCollectionTabId,
    activeSchemaTabId: state.activeSchemaTabId,
    activeDbManagerTabId: state.activeDbManagerTabId,
    activeTabType: state.activeTabType,
  });
}

export function isValidWorkspaceTabSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return false;
  return Boolean(
    snapshot.requestTabs?.length
    || snapshot.collectionTabs?.length
    || snapshot.schemaTabs?.length
    || snapshot.dbManagerTabs?.length
  );
}
